import React, { useState, ChangeEvent, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid, makeStyles, Paper, Typography } from '@material-ui/core';
import { ControlPointRounded, Close } from '@material-ui/icons';
import cx from 'clsx';
import NoAvailableLicensesMessage from 'components/@common/ProductLicenses/NoAvailableLicensesMessage';
import fp from 'lodash/fp';
import useProductCache from 'store/cache/product-cache';
import {
  LicenseAction,
  OrganizationLicenseUsage,
  ProductType,
  ProductCurriculum,
  Product
} from 'types/__generated__/graphql';
import { getProductNameAndIcon } from 'utilities/product-utils';
import LicenseAssignmentStatus from './LicenseAssignmentStatus';
import StudentCurriculumSelect from './StudentCurriculumSelect';
import UnsavedChangeIcon from './UnsavedChangeIcon';
import getPendingUpdateResult, { PendingUpdateResult } from './util/get-pending-update-result';
import { useProductCardValidation } from './product-card-validation-provider';

export interface LicenseUpdate {
  productFamilyId: number;
  productCurriculumId: number;
  action: LicenseAction;
}

interface StudentProductLicenseCardProps {
  productFamilyId: number;
  assignable?: boolean;
  organizationLicenseUsage: OrganizationLicenseUsage[];
  assignedProductCurriculumId?: number;
  pendingUpdates?: LicenseUpdate[];
  otherPendingUpdates?: LicenseUpdate[];
  gradeLevel?: string;
  error?: boolean;
  onLicenseUnassign: (productFamilyId: number, productCurriculumId: number) => void;
  onLicenseAssign: (productFamilyId: number, productCurriculumId: number) => void;
}

interface LicenseCountProps {
  product: Product;
  singleProduct?: boolean;
  pendingUpdateResult: PendingUpdateResult;
}

const StudentProductLicenseCard = ({
  productFamilyId,
  assignable = false,
  organizationLicenseUsage,
  assignedProductCurriculumId,
  pendingUpdates = [],
  otherPendingUpdates = [],
  gradeLevel,
  error = false,
  onLicenseAssign,
  onLicenseUnassign
}: StudentProductLicenseCardProps) => {
  const classes = useStyles();
  const { t } = useTranslation('StudentProductLicenseCard');
  const [{ findProductsByProductFamilyIdAndType, findProductFamilyById }] = useProductCache();
  const [, { addInvalidProductFamilyId, clearInvalidProductFamilyId }] = useProductCardValidation();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (error && containerRef.current) containerRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [error]);

  const productFamily = findProductFamilyById(productFamilyId);
  const licenseUsage = organizationLicenseUsage.filter(
    x => x.product.family.id === productFamily?.id && x.product.type === ProductType.STUDENT
  );

  const products = fp.flow(
    fp.filter((product: Product) => licenseUsage.some(lu => lu.product.id === product.id)),
    fp.orderBy<Product>('id', ['desc'])
  )(findProductsByProductFamilyIdAndType(productFamilyId, ProductType.STUDENT)) as Product[];

  const { curriculums, curriculumProductMap } = products.reduce(
    (acc, product) => {
      const productCurriculums = product.curriculums ?? [];
      productCurriculums.forEach(curriculum =>
        acc.curriculumProductMap.set(curriculum.id, product.id)
      );

      return {
        curriculums: [...acc.curriculums, ...productCurriculums],
        curriculumProductMap: acc.curriculumProductMap
      };
    },
    {
      curriculums: [] as ProductCurriculum[],
      curriculumProductMap: new Map<number, number>()
    }
  );

  const [showCurriculumSelect, setShowCurriculumSelect] = useState(
    !!assignedProductCurriculumId && curriculums.length > 1
  );

  const pendingAssignedCurriculumId = useMemo(() => {
    const assignmentUpdate = pendingUpdates.find(u => u.action === LicenseAction.ASSIGN);
    if (assignmentUpdate) return assignmentUpdate.productCurriculumId;

    const unassignmentUpdate = pendingUpdates.find(u => u.action === LicenseAction.UNASSIGN);
    if (unassignmentUpdate) return '';

    return assignedProductCurriculumId ?? '';
  }, [assignedProductCurriculumId, pendingUpdates]);

  const pendingUpdateResults = products.reduce((acc, product) => {
    const productLicenseUsage = licenseUsage.filter(x => x.product.id === product.id);
    const productPendingUpdates = [...pendingUpdates, ...otherPendingUpdates].filter(
      u => curriculumProductMap.get(u.productCurriculumId) === product.id
    );

    return acc.set(product.id, getPendingUpdateResult(productLicenseUsage, productPendingUpdates));
  }, new Map<number, PendingUpdateResult>());

  const { canAssign, showAvailableCount } = getPendingUpdateResult(licenseUsage, [
    ...pendingUpdates,
    ...otherPendingUpdates
  ]);

  if (!productFamily) return null;

  // TODO: This should probably be at the product-level, but for now we're allowing it if they have the privlege for *any* product
  // const canUnassignAny = privilegesCache.isPrivileged(privilegesGroups.assignOrgLicense);

  const handleCurriculumSelect = (
    e: ChangeEvent<{ name?: string | undefined; value: unknown }>
  ) => {
    const value = e.target.value;
    if (!value) return;

    const updatedProductCurriculumId = +(value as string);
    if (pendingAssignedCurriculumId === updatedProductCurriculumId) return;
    if (pendingAssignedCurriculumId && pendingAssignedCurriculumId !== updatedProductCurriculumId) {
      onLicenseUnassign(productFamily.id, pendingAssignedCurriculumId);
    }

    clearInvalidProductFamilyId(productFamilyId);
    onLicenseAssign(productFamily.id, +updatedProductCurriculumId);
  };

  const handleAssignButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (curriculums.length === 1) {
      onLicenseAssign(productFamily.id, curriculums[0].id);
    } else if (gradeLevel) {
      const gradeLevelCurriculums = curriculums.filter(c => c.gradeLevel?.includes(gradeLevel));

      if (gradeLevelCurriculums.length === 1) {
        onLicenseAssign(productFamily.id, gradeLevelCurriculums[0].id);
      } else {
        addInvalidProductFamilyId(productFamilyId);
      }
    }

    setShowCurriculumSelect(true);
  };

  const handleUnassignButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (pendingAssignedCurriculumId) {
      onLicenseUnassign(productFamily.id, pendingAssignedCurriculumId);
    }

    clearInvalidProductFamilyId(productFamilyId);
    setShowCurriculumSelect(false);
  };

  const { iconUrl, name } = getProductNameAndIcon(productFamily, ProductType.STUDENT);

  const disabledProductIds = products
    .filter(product => !pendingUpdateResults.get(product.id)?.canAssign)
    .map(product => product.id);

  return (
    <Paper
      ref={containerRef}
      className={cx(
        classes.root,
        pendingAssignedCurriculumId && classes.rootAssigned,
        error && classes.borderFlash
      )}
    >
      <Grid container spacing={1} className={classes.container}>
        <Grid item>
          <Box className={classes.updateContainer}>
            {pendingUpdates.length > 0 && <UnsavedChangeIcon />}
          </Box>
          {iconUrl && <img alt="" src={iconUrl} className={classes.productIcon} />}
        </Grid>
        <Grid item container alignContent="flex-start" xs>
          <Grid item>
            <Typography variant="h3">{name}</Typography>
          </Grid>
          {showAvailableCount && (
            <Grid item xs={12}>
              <Box display="flex" flexDirection="column">
                {products.map(product => {
                  const pendingUpdateResult = pendingUpdateResults.get(product.id);
                  if (!pendingUpdateResult) return null;

                  return (
                    <LicenseCount
                      key={`product_${product.id})_license_count`}
                      product={product}
                      pendingUpdateResult={pendingUpdateResult}
                      singleProduct={products.length === 1}
                    />
                  );
                })}
              </Box>
            </Grid>
          )}
          {showCurriculumSelect && curriculums.length > 1 && (
            <Grid item container className={classes.actionContainer}>
              <Grid item xs={12} sm="auto" className={classes.productLabelContainer}>
                <Typography variant="subtitle1">{t('Product')}:</Typography>
              </Grid>
              <Grid item xs={12} sm={5}>
                <StudentCurriculumSelect
                  productFamilyId={productFamilyId}
                  products={products}
                  disabledProductIds={disabledProductIds}
                  onChange={handleCurriculumSelect}
                  value={pendingAssignedCurriculumId}
                />
              </Grid>
            </Grid>
          )}
          {assignable && canAssign && !showCurriculumSelect && !pendingAssignedCurriculumId && (
            <Grid item xs={12} className={classes.actionContainer}>
              <Button
                aria-label="Assign student license button"
                className={classes.assignButton}
                onClick={handleAssignButtonClick}
                color="primary"
                disableElevation
                startIcon={<ControlPointRounded />}
                size="small"
                variant="contained"
              >
                {t('Assign license')}
              </Button>
            </Grid>
          )}
          {!canAssign && !showCurriculumSelect && !pendingAssignedCurriculumId && (
            <Grid item xs={12}>
              <NoAvailableLicensesMessage />
            </Grid>
          )}
        </Grid>
        <Grid item>
          {!!pendingAssignedCurriculumId ? (
            <Grid
              container
              direction="column"
              alignItems="flex-end"
              justify="space-between"
              className={classes.statusContainer}
            >
              <Grid item>
                <LicenseAssignmentStatus assigned />
              </Grid>
              {assignable && (
                <Grid item>
                  <Button
                    aria-label="Unassign student license button"
                    className={classes.assignButton}
                    onClick={handleUnassignButtonClick}
                    color="primary"
                    disableElevation
                    startIcon={<Close />}
                    size="small"
                    variant="contained"
                  >
                    {t('Unassign license')}
                  </Button>
                </Grid>
              )}
            </Grid>
          ) : (
            <LicenseAssignmentStatus assigned={false} />
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

const LicenseCount = ({
  product,
  pendingUpdateResult,
  singleProduct = false
}: LicenseCountProps) => {
  const classes = useStyles();
  const { t } = useTranslation('StudentProductLicenseCard');

  return (
    <Typography
      key={`product_${product.id}_count`}
      variant="subtitle1"
      className={classes.licenseCountText}
    >
      {pendingUpdateResult.willUseOverage
        ? t(singleProduct ? 'overageLicenses' : 'overageProductLicenses', {
            count: pendingUpdateResult.potentialAvailableCountAfterUpdate,
            productName: product.name
          })
        : t(singleProduct ? 'availableLicenses' : 'availableProductLicenses', {
            count: pendingUpdateResult.realAvailableCountAfterUpdate,
            productName: product.name
          })}
    </Typography>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    border: '2px solid transparent',
    padding: theme.spacing(2)
  },
  rootAssigned: {
    background: '#E7EFEA'
  },
  borderFlash: {
    animation: `$flash 1000ms ${theme.transitions.easing.easeInOut} 0s 3`
  },
  '@keyframes flash': {
    '0%': {
      border: '2px solid inherit'
    },
    '50%': {
      border: `2px solid ${theme.palette.error.light}`
    },
    '100%': {
      border: '2px solid inherit'
    }
  },
  container: { position: 'relative' },
  productIcon: {
    padding: theme.spacing(1),
    height: theme.typography.pxToRem(80),
    width: theme.typography.pxToRem(80)
  },
  productLabelContainer: {
    paddingRight: theme.spacing(1)
  },
  actionContainer: {
    paddingTop: theme.spacing(4)
  },
  updateContainer: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  statusContainer: {
    height: '100%',
    paddingBottom: theme.spacing(1)
  },
  licenseCountText: {
    fontWeight: 300
  },
  assignButton: {
    background: '#FFFFFF',
    border: '1px solid #969696',
    color: '#2D2D2D',
    '&:hover': {
      background: '#FFFFFF'
    },
    padding: theme.typography.pxToRem(20)
  }
}));

export default StudentProductLicenseCard;
